package com.trevorwiebe.timesheet.mileage.presentation

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.trevorwiebe.timesheet.core.domain.formatWithCommas
import com.trevorwiebe.timesheet.core.domain.model.UserRole
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.CollapsingHeaderLayout
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.PhotoViewerDialog
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.botPadding
import com.trevorwiebe.timesheet.mileage.domain.model.locationName
import com.trevorwiebe.timesheet.mileage.presentation.components.MileageEntryListItem
import com.trevorwiebe.timesheet.mileage.presentation.components.MileageFilterBar
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun MileageScreen(
    viewModel: MileageViewModel = koinViewModel()
) {
    val uiState by viewModel.uiState.collectAsState()
    val bottomPadding = botPadding()

    val isElevated = uiState.currentUser?.role == UserRole.ADMIN ||
        uiState.currentUser?.role == UserRole.DISPATCHER

    Scaffold(
        topBar = {
            TopBar(
                title = "Mileage Log",
                actions = {
                    val filter = uiState.filter
                    if (
                        filter.equipmentId != null ||
                        filter.tripTypes.isNotEmpty() ||
                        filter.employeeId != null
                    ) {
                        TextButton(onClick = { viewModel.onEvent(MileageEvent.OnClearFilters) }) {
                            Text("Clear Filters", color = MaterialTheme.colorScheme.tertiary)
                        }
                    }
                    MileageTopBarAddAction(
                        onClick = { viewModel.onEvent(MileageEvent.OnShowAddDialog) }
                    )
                }
            )
        }
    ) { innerPadding ->
        ResponsiveContent(modifier = Modifier.padding(innerPadding)) {
        CollapsingHeaderLayout(
            header = {
                    Column {
                        MileageFilterBar(
                            filter = uiState.filter,
                            equipment = uiState.equipment,
                            users = uiState.userList,
                            isElevated = isElevated,
                            onEmployeeSelected = { viewModel.onEvent(MileageEvent.OnFilterByEmployee(it)) },
                            onEquipmentSelected = { viewModel.onEvent(MileageEvent.OnFilterByEquipment(it)) },
                            onTripTypesChanged = { viewModel.onEvent(MileageEvent.OnFilterByTripType(it)) },
                            onStartDateClick = { viewModel.onEvent(MileageEvent.OnStartDateClick) },
                            onEndDateClick = { viewModel.onEvent(MileageEvent.OnEndDateClick) },
                            onStartDateSelected = { viewModel.onEvent(MileageEvent.OnStartDateSelected(it)) },
                            onEndDateSelected = { viewModel.onEvent(MileageEvent.OnEndDateSelected(it)) },
                            onDismissStartPicker = { viewModel.onEvent(MileageEvent.OnDismissStartPicker) },
                            onDismissEndPicker = { viewModel.onEvent(MileageEvent.OnDismissEndPicker) },
                        )
                        HorizontalDivider()
                    }
                },
                pinnedHeader = {
                    Column {
                        MileageTableHeader()
                        HorizontalDivider()
                    }
                },
                fab = {
                    MileageFab(onClick = { viewModel.onEvent(MileageEvent.OnShowAddDialog) })
                }
            ) { contentPadding ->
                when {
                    uiState.isLoading -> Box(modifier = Modifier.fillMaxSize()) {
                        CircularProgressIndicator(
                            modifier = Modifier.align(Alignment.Center),
                            color = MaterialTheme.colorScheme.tertiary
                        )
                    }
                    uiState.entries.isEmpty() -> Box(modifier = Modifier.fillMaxSize()) {
                        Text(
                            text = "No mileage entries match the current filters.",
                            modifier = Modifier.align(Alignment.Center)
                        )
                    }
                    else -> LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = contentPadding
                    ) {
                        items(uiState.entries) { entry ->
                            val equipmentName = uiState.equipment
                                .firstOrNull { it.id == entry.equipmentId }
                                ?.unitNumber ?: entry.equipmentId
                            val canEdit = isElevated || entry.employeeId == uiState.currentUser?.id
                            MileageEntryListItem(
                                entry = entry,
                                equipmentName = equipmentName,
                                locationName = entry.locationName(uiState.locations),
                                onEdit = if (canEdit) {
                                    { viewModel.onEvent(MileageEvent.OnShowEditDialog(entry)) }
                                } else null,
                            )
                        }
                        item { Spacer(Modifier.height(bottomPadding)) }
                    }
                }
            }
        }
    }
    if (uiState.showAddDialog) {
        AddMileageEntryDialog(
            uiState = uiState,
            onEvent = viewModel::onEvent
        )
    }

    if (uiState.showPhotoViewer) {
        PhotoViewerDialog(
            imageBytes = uiState.pendingReceiptBytes?.bytes,
            isLoading = uiState.isLoadingReceiptImage,
            onSave = { rotated -> viewModel.onEvent(MileageEvent.OnPhotoSaved(rotated)) },
            onDelete = { viewModel.onEvent(MileageEvent.OnPhotoDeleted) },
            onDismiss = { viewModel.onEvent(MileageEvent.OnDismissPhotoViewer) }
        )
    }

    val warningEquipmentName = uiState.equipment
        .firstOrNull { it.id == uiState.pendingEntry?.equipmentId }
        ?.unitNumber
    val odometerWarningMessage =
        "This entry is ${uiState.pendingOdometerDifference?.formatWithCommas() ?: "500+"} miles above the previous entry" +
            (if (warningEquipmentName != null) " for truck: $warningEquipmentName" else "") +
            ". Is that correct?"
    NativeDialog(
        visible = uiState.showOdometerWarning,
        title = "Unusually High Mileage",
        message = odometerWarningMessage,
        confirmText = "Yes, save it",
        onConfirm = { viewModel.onEvent(MileageEvent.OnConfirmOdometerWarning) },
        dismissText = "Go back",
        onDismiss = { viewModel.onEvent(MileageEvent.OnDismissOdometerWarning) }
    )

    NativeDialog(
        visible = uiState.showDeleteConfirm,
        title = "Delete Entry",
        message = "Delete this mileage entry? This action cannot be undone.",
        confirmText = "Delete",
        onConfirm = { viewModel.onEvent(MileageEvent.OnConfirmDelete) },
        dismissText = "Cancel",
        onDismiss = { viewModel.onEvent(MileageEvent.OnDismissDelete) }
    )
}

@Composable
private fun MileageTableHeader() {
    Surface(color = MaterialTheme.colorScheme.background) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 12.dp, vertical = 10.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = "Date/Time",
                modifier = Modifier.weight(1.5f),
                fontWeight = FontWeight.Bold,
                fontSize = 13.sp,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            Text(
                text = "Trip Type",
                modifier = Modifier.weight(3f),
                fontWeight = FontWeight.Bold,
                fontSize = 13.sp,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            Text(
                text = "Miles",
                modifier = Modifier.weight(1.5f),
                fontWeight = FontWeight.Bold,
                fontSize = 13.sp,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                textAlign = TextAlign.End
            )
            Text(
                text = "Truck",
                modifier = Modifier.weight(1f),
                fontWeight = FontWeight.Bold,
                fontSize = 13.sp,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                textAlign = TextAlign.End
            )
        }
    }
}
